import '../css/Landing.css';
import { isNil } from 'lodash';
import React, { FormEvent } from 'react';
import { connect } from 'react-redux';
import { Alert, Button, Col, Container, Form, FormGroup, Input, Row } from 'reactstrap';
import { SendSmsWithOneLinkAttributionReq } from '../../../actions/oneLinkActions';
import { AppState, UtmData } from '../../../store';
import { numMembersText } from '../../../utils/configuration';
import { trackEvent } from '../../../utils/tracking';
import { FooterLink } from '../../styling/Typography';

interface State {
  number: string;
  success: boolean;
  error: string;
}

const DEFAULT_UTM_PARAM = '';
const IMPACT_RADIUS_MEDIA_SOURCE = 'impactradius_int';
const DEFAULT_PID = 'website_int';

/** Same order as clickid coalescing: first non-nullish URL param wins. */
const CLICK_ID_RESOLUTION_ORDER = [
  'irclickid',
  'fbclid',
  'ttclid',
  'sccid',
  'gclid',
  'msclkid',
  'rclid',
] as const;

const CLICK_PLATFORM_BY_PARAM = {
  irclickid: 'Impact',
  fbclid: 'Meta',
  ttclid: 'Tiktok',
  sccid: 'Snapchat',
  gclid: 'Google',
  msclkid: 'Bing',
  rclid: 'Reddit',
};

function resolveClickIdAndPlatform(
  props: UtmData,
): {
  clickId: string | undefined;
  clickPlatform: string | undefined;
} {
  const key = CLICK_ID_RESOLUTION_ORDER.find((k) => props[k] !== null && props[k] !== undefined);
  if (key === undefined) {
    return { clickId: undefined, clickPlatform: undefined };
  }
  return {
    clickId: props[key] as string,
    clickPlatform: CLICK_PLATFORM_BY_PARAM[key],
  };
}

class TextMeTheApp extends React.Component<UtmData, State> {
  handleNumberChange = (e) => {
    this.setState({ number: e.target.value });
  };

  constructor(props) {
    super(props);
    this.state = {
      number: '',
      success: false,
      error: null,
    };
  }

  submitNumber = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    trackEvent('text_me_the_app', 'click', 'text_me_the_app');

    // if the user has an irclickid value, we can assume that they clicked on an Impact ad/link.
    // we should set pid to "impactradius_int" so AppsFlyer can attribute the traffic as Impact-generated.
    const { irclickid } = this.props;
    const pid = !isNil(irclickid) ? IMPACT_RADIUS_MEDIA_SOURCE : DEFAULT_PID;

    const { clickId, clickPlatform } = resolveClickIdAndPlatform(this.props);

    const createOneLinkAndSendSmsRequest: SendSmsWithOneLinkAttributionReq = {
      c: this.props.campaignName || DEFAULT_UTM_PARAM,
      af_adset: this.props.campaignContent || DEFAULT_UTM_PARAM,
      af_channel: this.props.campaignSource || clickPlatform || DEFAULT_UTM_PARAM,
      phoneNumber: this.state.number,
      clickid: clickId,
      pid,
      deep_link_value: this.props.deepLinkValue,
    };

    return fetch('/api/download/v3/link', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
      },
      body: JSON.stringify(createOneLinkAndSendSmsRequest),
    })
      .then((res) => {
        if (!res.ok) {
          return res.json();
        } else {
          this.setState({ success: true });
          return null;
        }
      })
      .then((e) => {
        if (e) {
          this.setState({ error: e.message });
        }
      });
  };

  render() {
    return (
      <Container fluid className="landing-container">
        <Row>
          <Col lg={{ size: 4, offset: 1 }} sm="12" xl={{ size: 5, offset: 1 }}>
            <div className="pt-3 mb-5 pb-5">
              <img
                style={{ width: `150px`, height: 'auto' }}
                src="/rebrand/brigit-header-logo-2024.png"
              />
            </div>
            <div>
              <div className="pt-2 mb-3">
                <h2 className="header-text stash-hero-text-size mb-3">Get Brigit</h2>
              </div>
              <div className="info-text stash-dangle-margin">
                Get cash fast<sup>1</sup> and join {numMembersText}+ people who are using Brigit to
                access cash when they need it, build their credit <sup>2</sup>, and improve their
                finances. It only takes 2 minutes to sign up.
                <br />
                <br />
                No credit check. No late fees. No tipping.
              </div>
              {this.state.error && (
                <div className="pr-5">
                  <Alert color="danger">{this.state.error}</Alert>
                </div>
              )}
              <div className="info-text my-3">
                {!this.state.success && (
                  <Form onSubmit={this.submitNumber}>
                    <FormGroup className="email-form-group">
                      <Input
                        placeholder="Enter Phone Number"
                        value={this.state.number}
                        onChange={this.handleNumberChange}
                        style={{ height: '55px' }}
                      />
                      {this.state.number.length >= 10 ? (
                        <Button onClick={this.submitNumber} className="get-button px-5">
                          <div style={{ fontSize: '18px' }} className="text-white">
                            Text me the link
                          </div>
                        </Button>
                      ) : (
                        <Button disabled className="get-button px-5">
                          <div style={{ fontSize: '18px' }} className="text-white">
                            Text me the link
                          </div>
                        </Button>
                      )}
                    </FormGroup>
                    <div className="info-text stash-dangle-margin mr-lg-4 y-3">
                      You will receive a one time SMS to download the app.
                      <br />
                      <a style={{ color: '#444' }} href="https://www.hellobrigit.com/privacy">
                        Privacy Policy
                      </a>
                      <br />
                      <a style={{ color: '#444' }} href="https://www.hellobrigit.com/terms">
                        Terms of Service
                      </a>
                    </div>
                  </Form>
                )}
                {this.state.success && (
                  <div className="pr-5">
                    <Alert color="success">Text Sent!</Alert>
                  </div>
                )}
              </div>
            </div>
          </Col>
          <Col lg="7" xl="6" className="px-0">
            <img
              style={{ width: '100%' }}
              className="top-picture"
              src="/pr-redesign/banners/TextMeTheApp_BannerBackgroundDesktop.png"
            />
            <img
              style={{ width: '100%' }}
              className="bottom-picture"
              src="/pr-redesign/banners/TextMeTheApp_BannerBackgroundMobile.png"
            />
          </Col>
        </Row>
        <Row style={{ backgroundColor: '#24956A', color: '#FFFFFF', minHeight: '368px' }}>
          <div>
            <div style={{ marginTop: '20px' }}>
              <img
                style={{ width: '160px', margin: 'auto', display: 'block' }}
                src="/rebrand/brigit-white-footer-logo-2024.png"
              />
            </div>
            <div style={{ width: '80%', margin: 'auto', marginTop: '20px' }}>
              <div>
                <FooterLink
                  onClick={() => trackEvent('footer-privacy', 'click', 'footer-privacy')}
                  className="links"
                  href="https://www.hellobrigit.com/privacy"
                >
                  Privacy Policy
                </FooterLink>
              </div>
              <div className="mb-1">
                <FooterLink
                  onClick={() => trackEvent('footer-tos', 'click', 'footer-tos')}
                  className="links"
                  href="https://www.hellobrigit.com/terms"
                >
                  Terms of Service
                </FooterLink>
              </div>
            </div>
            <div
              style={{ width: '80%', margin: 'auto', marginTop: '20px' }}
              className="t-and-c-text"
            >
              <p>
                <sup>1</sup> Not all users will qualify for advances; Depending on eligibility,
                advances range from $25-$500. Monthly subscription required; ranging from
                $8.99-$15.99. Cancel anytime. Instant Cash is not available in all states.
              </p>

              <p>
                <sup>2</sup> Impact to score may vary. Some users’ scores may not improve. Results
                will depend on many factors, including on-time payment history, the status of
                non-Brigit accounts, and financial history. Results show that customers with a
                starting credit score of 600 or below were more likely to see positive score change
                results. A monthly subscription is required; $15.99. Cancel anytime. Credit Builder
                loans are not available in all states.
              </p>

              <p>
                The Brigit Credit Builder is a service provided by Brigit and its bank partner,
                Coastal Community Bank, Member FDIC. The Brigit Credit Builder product is separate
                from the Brigit Instant Cash Advance service. Brigit Credit Builder installment
                loans are issued by Coastal Community Bank, Member FDIC, subject to approved
                underwriting practices. Brigit is a financial technology company, not a bank. FDIC
                insurance only covers the failure of an FDIC-insured bank. FDIC insurance is
                available through pass-through insurance at Coastal Community Bank if certain
                conditions have been met.
              </p>

              <p>
                © 2017-{new Date().getFullYear()} Bridge It, Inc. Brigit™ is a trademark of Bridge
                It, Inc. All Rights Reserved. Product name, logo, brands, and other trademarks
                featured or referred to within Brigit are the property of their respective trademark
                holders.
              </p>
            </div>
          </div>
        </Row>
        <img
          src="https://di.rlcdn.com/api/segment?pid=713260&pdata=overseerteam_id%3Dteam_0ee4eba271%2Cgroup%3D5808%2Cpagetype%3Dthankyou"
          width="1"
          height="1"
          style={{ display: 'none' }}
          alt=""
          className="no-lazy"
          data-no-lazy="1"
        />
      </Container>
    );
  }
}

const mapStateToProps = (state: AppState) => {
  const { utmData } = state;
  return { ...utmData };
};

export const TextMeTheAppConnected = connect(mapStateToProps, null)(TextMeTheApp);
